/**
 * The crop season(s) attached to an invoice are derived from the current
 * Breadcrumb selection (persisted app-wide in `localStorage.selectedCropSeasons`
 * as `[{ id, name }]`) rather than a manual picker in the flow — see FR-069.
 *
 * This is the single source of truth for both the create and edit flows: the
 * invoice is saved with whatever seasons are selected in the breadcrumb at save
 * time. Reading it here (instead of a seeded Formik field) means the saved value
 * always tracks the breadcrumb no matter which step the user saves from.
 */
export const getSelectedCropSeasonIds = (): string[] => {
  try {
    const selected = JSON.parse(
      localStorage.getItem('selectedCropSeasons') ?? '[]'
    );
    // The Breadcrumb persists the selection as an array of raw season-id
    // strings (e.g. `["1876c1c1-…"]`) — the same shape the rest of the app
    // reads (Invoices list, Property, Tasks…). An `{ id }` object shape is
    // tolerated too in case a caller ever stores the richer form.
    //
    // Drop entries without a real id (e.g. an "all / no current season"
    // placeholder) — a null/undefined season id becomes a `null` seasonId in
    // the API payload and NPEs the invoice service's season lookup (FR-095).
    return (selected ?? [])
      .map((cs: string | { id?: string } | null) =>
        typeof cs === 'string' ? cs : cs?.id
      )
      .filter(Boolean);
  } catch {
    return [];
  }
};

export default getSelectedCropSeasonIds;
